import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import ModalCenterLayout from "../modal/modal.center.layout";

interface Props {
  generatePdf: () => Promise<{ blob: Blob; filename: string }>;
  onClose: () => void;
}

const ReportPdfPreviewModal = ({ generatePdf, onClose }: Props) => {
  const [document, setDocument] = useState<{ url: string; filename: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [entered, setEntered] = useState(false);
  const loadingRef = useRef<HTMLDivElement>(null);
  const close = () => { if (!loading) onClose(); };

  useEffect(() => {
    if (!loading) return;
    loadingRef.current?.focus();
    const blockInteraction = (event: Event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const events = ["click", "pointerdown", "keydown"];
    events.forEach(type => window.document.addEventListener(type, blockInteraction, true));
    return () => events.forEach(type => window.document.removeEventListener(type, blockInteraction, true));
  }, [loading]);

  useEffect(() => {
    if (!entered) return;
    let active = true;
    let objectUrl: string | undefined;
    setLoading(true);
    setError(false);
    setDocument(null);
    let timer: ReturnType<typeof setTimeout>;
    const frame = requestAnimationFrame(() => {
      timer = setTimeout(() => {
    generatePdf().then(({ blob, filename }) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setDocument({ url: objectUrl, filename });
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
      }, 0);
    });
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [generatePdf, retry, entered]);

  return <ModalCenterLayout isVisible closedHandler={close} size="l" onEntered={() => setEntered(true)}>
    <Preview role="dialog" aria-modal="true" aria-labelledby="report-pdf-preview-title" aria-busy={loading}>
      <div className="preview-header">
        <h3 id="report-pdf-preview-title">PDF 미리보기</h3>
        <div className="preview-actions">
          {!loading && document && <a href={document.url} download={document.filename}>PDF 다운로드</a>}
          <button type="button" aria-label="PDF 미리보기 닫기" disabled={loading} onClick={close}>×</button>
        </div>
      </div>
      {loading && <div className="preview-loading" role="status" aria-live="polite" tabIndex={-1} ref={loadingRef}>
        <span className="preview-spinner" aria-hidden="true" />
        <p>PDF를 생성하고 있습니다...</p>
        <span>완료될 때까지 잠시 기다려주세요.</span>
      </div>}
      {error && <div className="preview-status" role="alert">PDF 미리보기를 생성하지 못했습니다. <button type="button" onClick={() => { setLoading(true); setError(false); setRetry(value => value + 1); }}>다시 시도</button></div>}
      {!loading && document && <>
        <iframe title="백업 리포트 PDF 미리보기" src={document.url} />
        <div className="preview-footer">미리보기가 표시되지 않으면 <a href={document.url} target="_blank" rel="noopener noreferrer">새 창에서 보기</a>를 이용해주세요.</div>
      </>}
    </Preview>
  </ModalCenterLayout>;
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Preview = styled.div`
  display: flex;
  flex-direction: column;
  height: 85vh;
  background: white;
  .preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.6rem 2rem;
    background: #333333;
    color: white;
    h3 { font-size: 1.8rem; }
  }
  .preview-actions {
    display: flex;
    align-items: center;
    gap: 1.6rem;
    a { padding: 0.8rem 1.2rem; border-radius: 0.6rem; background: #30a690; color: white; font-size: 1.4rem; }
    button { border: 0; background: none; color: white; font-size: 2.4rem; cursor: pointer; }
  }
  iframe { flex: 1; min-height: 0; width: 100%; border: 0; }
  .preview-loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1.6rem;
    outline: none;
    p { font-size: 1.6rem; color: #333333; }
    > span:last-child { font-size: 1.3rem; color: #777777; }
  }
  .preview-spinner {
    width: 4.8rem;
    height: 4.8rem;
    border: 4px solid #e3e9e8;
    border-top-color: #30a690;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }
  .preview-actions button:disabled { opacity: 0.35; cursor: wait; }
  .preview-status { padding: 3rem; font-size: 1.4rem; }
  .preview-footer { padding: 1rem 2rem; font-size: 1.2rem; a { color: #227465; text-decoration: underline; } }
`;

export default ReportPdfPreviewModal;
